import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useSEO } from "../lib/seo";
import { CheckCircle2, X, Star, Users, FileText } from "lucide-react";

/** Compare up to 3 courses side-by-side. URL: /compare?ids=slug1,slug2,slug3 */
export default function Compare() {
  const [params, setParams] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const [courses, setCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);

  useEffect(() => {
    api.get("/courses?limit=100").then((r) => setAllCourses(r.data));
  }, []);

  useEffect(() => {
    if (!ids.length) { setCourses([]); return; }
    Promise.all(ids.map((slug) => api.get(`/courses/${slug}`).then((r) => r.data).catch(() => null)))
      .then((arr) => setCourses(arr.filter(Boolean)));
    // eslint-disable-next-line
  }, [params.get("ids")]);

  useSEO({
    title: courses.length ? `Compare ${courses.map((c) => c.title).join(" vs ")}` : "Compare practice tests",
    description: "Side-by-side comparison of certification practice tests on BeforeExam.",
  });

  const addCourse = (slug) => {
    const next = [...new Set([...ids, slug])].slice(0, 3);
    setParams({ ids: next.join(",") });
  };
  const remove = (slug) => setParams({ ids: ids.filter((x) => x !== slug).join(",") });

  return (
    <div data-testid="compare-page">
      <section className="border-b border-border bg-[color:var(--brand-cream-2)]">
        <div className="container-x py-10">
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">Compare <span className="font-serif text-gradient-gold">practice tests</span></h1>
          <p className="mt-1 text-sm text-muted-foreground">Pick up to 3 courses to compare side-by-side.</p>
        </div>
      </section>

      <section className="container-x py-10">
        {courses.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <h3 className="font-display text-xl">Add your first course to compare</h3>
            <p className="text-sm text-muted-foreground mt-1">Use the picker below.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="compare-table">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-3 w-44 align-bottom"></th>
                  {courses.map((c) => (
                    <th key={c.id} className="text-left p-3 align-bottom">
                      <div className="flex items-start gap-2">
                        <img src={c.image_url} alt="" className="h-12 w-16 rounded object-cover" />
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-base line-clamp-2">{c.title}</div>
                          <div className="text-xs text-muted-foreground">{c.vendor}</div>
                        </div>
                        <button onClick={() => remove(c.slug)} className="text-muted-foreground hover:text-foreground" aria-label="Remove" data-testid={`remove-compare-${c.slug}`}><X className="h-4 w-4" /></button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Price", (c) => `$${c.price.toFixed(2)}`],
                  ["Original price", (c) => `$${c.original_price.toFixed(2)}`],
                  ["Difficulty", (c) => c.difficulty],
                  ["Mock exams", (c) => c.test_count],
                  ["Questions", (c) => c.question_count],
                  ["Duration", (c) => `${c.duration_minutes} min`],
                  ["Pass score", (c) => `${c.pass_score}%`],
                  ["Rating", (c) => (<span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-yellow-500 stroke-yellow-500" /> {(c.rating_avg || 4.7).toFixed(1)}</span>)],
                  ["Students", (c) => (c.students_count || 0).toLocaleString()],
                  ["Bestseller", (c) => c.bestseller ? <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success)]" /> : "—"],
                  ["Updated 2026", (c) => c.recently_updated ? <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success)]" /> : "—"],
                ].map(([label, fn]) => (
                  <tr key={label} className="border-t border-border">
                    <th className="text-left p-3 text-[11px] uppercase tracking-wider font-bold text-muted-foreground align-top">{label}</th>
                    {courses.map((c) => (
                      <td key={c.id} className="p-3 align-top">{fn(c)}</td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t border-border">
                  <th className="p-3"></th>
                  {courses.map((c) => (
                    <td key={c.id} className="p-3">
                      <Link to={`/courses/${c.slug}`}><button className="btn-primary h-10 text-xs">View course</button></Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {courses.length < 3 && (
          <div className="mt-10">
            <h3 className="font-display text-lg mb-3">Add another course</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {allCourses.filter((c) => !ids.includes(c.slug)).slice(0, 8).map((c) => (
                <button
                  key={c.id}
                  onClick={() => addCourse(c.slug)}
                  className="text-left rounded-md border border-border bg-card p-3 hover:border-[color:var(--brand-amber)] transition flex gap-2 items-center"
                  data-testid={`add-compare-${c.slug}`}
                >
                  <img src={c.image_url} alt="" className="h-10 w-14 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold line-clamp-1">{c.title}</div>
                    <div className="text-xs text-muted-foreground">${c.price.toFixed(2)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
